import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Search, Loader2, Navigation, X } from 'lucide-react';
import { streetsApi } from '../services/api';
import BusinessLocationMap from './BusinessLocationMap';

/**
 * AddressInput - Street autocomplete with geocoding and map preview.
 *
 * Props:
 *  - bairroId: selected bairro territory id (for street filtering)
 *  - bairroNome: selected bairro name (for display)
 *  - municipioNome: municipality name (for geocoding context)
 *  - rua: current street value
 *  - numero: current number value
 *  - complemento: current complement value
 *  - latitude/longitude: current coords
 *  - onChangeRua(value): callback
 *  - onChangeNumero(value): callback
 *  - onChangeComplemento(value): callback
 *  - onChangeCoords({latitude, longitude}): callback
 *  - municipioId: for creating new streets
 */
export default function AddressInput({
  bairroId,
  bairroNome,
  municipioNome,
  municipioId,
  municipioCenter,
  cep,
  rua,
  numero,
  complemento,
  latitude,
  longitude,
  onChangeRua,
  onChangeNumero,
  onChangeComplemento,
  onChangeCoords,
  hideGeocodeButton = false,
  ruaOptional = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  // 'exact' | 'partial' | 'city' | 'notfound' | 'kept' | null
  // 'kept' = geocode falhou mas mantivemos o pin anterior (não confundir usuário).
  const [geocodeStatus, setGeocodeStatus] = useState(null);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const autoGeocodedRef = useRef(false);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search streets as user types
  const searchStreets = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }
    setLoadingSuggestions(true);
    try {
      const params = { q: query, limit: 10 };
      if (bairroId) params.bairro_id = bairroId;
      if (municipioId) params.municipio_id = municipioId;
      const results = await streetsApi.search(params);
      setSuggestions(results);
      setShowSuggestions(true);
    } catch (e) {
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [bairroId, municipioId]);

  const handleRuaChange = (value) => {
    onChangeRua(value);
    setGeocodeStatus(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchStreets(value), 300);
  };

  const handleSelectSuggestion = (street) => {
    onChangeRua(street.nome);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Geocode: build full address and call API
  const handleGeocode = async () => {
    if (!rua) return;
    const addressParts = [rua];
    if (numero) addressParts[0] += `, ${numero}`;
    if (bairroNome) addressParts.push(bairroNome);

    setGeocoding(true);
    setGeocodeStatus(null);
    // Guarda o pin atual para poder restaurá-lo caso o geocode falhe — evita
    // dizer "endereço não encontrado" quando a busca anterior já achou o local.
    const previousLatitude = latitude;
    const previousLongitude = longitude;
    const hadPreviousPin = Number.isFinite(previousLatitude) && Number.isFinite(previousLongitude)
      && previousLatitude !== 0 && previousLongitude !== 0;
    try {
      const result = await streetsApi.geocode(
        addressParts.join(', '),
        municipioNome || '',
        'Minas Gerais',
        { cep }
      );
      if (result.found && Array.isArray(result.results) && result.results.length > 0) {
        const best = result.results[0];
        onChangeCoords({ latitude: best.latitude, longitude: best.longitude });
        setGeocodeStatus(result.match_level || 'exact');
      } else if (hadPreviousPin) {
        // Nova busca falhou, mas o usuário já tinha um pin anterior — não
        // sobrescrever nem confundir. Mostra o status "kept".
        setGeocodeStatus('kept');
      } else {
        setGeocodeStatus('notfound');
      }
    } catch (e) {
      // Preserva pin anterior em erro de rede/servidor.
      setGeocodeStatus(hadPreviousPin ? 'kept' : 'notfound');
    } finally {
      setGeocoding(false);
    }
  };

  // Auto-geocode: quando o ViaCEP terminou de preencher rua e o usuário
  // ainda não posicionou nada no mapa, faz o geocode automaticamente uma vez.
  // Reduz o clique extra em "Buscar localização pelo endereço" — que era o
  // principal ponto de fricção reportado por empreendedores.
  useEffect(() => {
    if (autoGeocodedRef.current) return;
    if (!rua || !bairroNome || !municipioNome) return;
    // Só auto-geocode se ainda NÃO temos pin — se o usuário mexeu manualmente
    // no mapa antes do CEP, respeita a decisão dele.
    const hasPin = Number.isFinite(latitude) && Number.isFinite(longitude)
      && latitude !== 0 && longitude !== 0;
    if (hasPin) { autoGeocodedRef.current = true; return; }
    autoGeocodedRef.current = true;
    handleGeocode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rua, bairroNome, municipioNome]);

  // Auto-save street to DB when user finishes typing a new street
  const handleRuaBlur = async () => {
    setShowSuggestions(false);
    if (rua && rua.length >= 3 && bairroId && municipioId) {
      try {
        await streetsApi.create({ nome: rua, bairro_id: bairroId, municipio_id: municipioId });
      } catch (e) {
        // silent - best effort
      }
    }
  };

  const hasCoords = latitude && longitude && (latitude !== 0 || longitude !== 0);

  return (
    <div className="space-y-4">
      {/* Street input with autocomplete */}
      <div ref={wrapperRef} className="relative">
        <label className="block text-sm font-medium mb-2">
          Rua / Logradouro {!ruaOptional && <span className="text-red-500">*</span>}
          {ruaOptional && <span className="text-gray-400 text-xs ml-1">(opcional)</span>}
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={rua}
            onChange={(e) => handleRuaChange(e.target.value)}
            onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
            onBlur={handleRuaBlur}
            placeholder="Digite o nome da rua..."
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] transition-all"
            data-testid="street-input"
          />
          {loadingSuggestions && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
          )}
        </div>

        {/* Suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto" data-testid="street-suggestions">
            {suggestions.map((street) => (
              <button
                key={street.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleSelectSuggestion(street); }}
                className="w-full text-left px-4 py-2.5 hover:bg-[var(--primary)]/5 flex items-center gap-2 text-sm transition-colors"
              >
                <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span>{street.nome}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Number + Complement */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-sm font-medium mb-2">Número</label>
          <input
            type="text"
            value={numero}
            onChange={(e) => { onChangeNumero(e.target.value); setGeocodeStatus(null); }}
            placeholder="Nº"
            className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] transition-all"
            data-testid="number-input"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-2">Complemento</label>
          <input
            type="text"
            value={complemento}
            onChange={(e) => onChangeComplemento(e.target.value)}
            placeholder="Apto, sala, bloco..."
            className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] transition-all"
            data-testid="complement-input"
          />
        </div>
      </div>

      {/* Geocode button + status */}
      {!hideGeocodeButton && (
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleGeocode}
          disabled={geocoding || !rua}
          className="flex items-center gap-2 px-4 py-2.5 bg-[var(--primary)] text-white rounded-xl hover:bg-[#450066] disabled:opacity-50 transition-all text-sm font-medium"
          data-testid="geocode-btn"
        >
          {geocoding ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Navigation className="w-4 h-4" />
          )}
          Buscar localização pelo endereço
        </button>

        {geocodeStatus === 'exact' && (
          <span className="text-sm text-green-600 flex items-center gap-1" data-testid="geocode-success">
            <MapPin className="w-4 h-4" /> Localização exata encontrada!
          </span>
        )}
        {geocodeStatus === 'partial' && (
          <span className="text-sm text-green-600 flex items-center gap-1" data-testid="geocode-success">
            <MapPin className="w-4 h-4" /> Localização aproximada (rua)
          </span>
        )}
        {geocodeStatus === 'city' && (
          <span className="text-sm text-amber-600 flex items-center gap-1" data-testid="geocode-success">
            <MapPin className="w-4 h-4" /> Localização da cidade (ajuste o pin se necessário)
          </span>
        )}
        {geocodeStatus === 'kept' && (
          <span className="text-sm text-amber-700 flex items-center gap-1" data-testid="geocode-kept">
            <MapPin className="w-4 h-4" /> Mantendo o pin anterior. Ajuste manualmente clicando no mapa se necessário.
          </span>
        )}
        {geocodeStatus === 'notfound' && (
          <span className="text-sm text-red-600 flex items-center gap-1" data-testid="geocode-notfound">
            Endereço não encontrado. Clique no mapa para posicionar manualmente.
          </span>
        )}
      </div>
      )}

      {/* Mapa interativo — SEMPRE visível. Se sem coords, começa centralizado
          no município e permite clique/arrastar. Se com coords, faz flyTo
          para o ponto (auto-refresh). */}
      <BusinessLocationMap
        latitude={latitude}
        longitude={longitude}
        municipioCenter={municipioCenter}
        onChangeCoords={onChangeCoords}
      />

      {/* Fallback manual de lat/lng removido a pedido do PO (iter 213).
          Coordenadas são setadas exclusivamente via geocoding do endereço,
          uso da localização atual ou clique no mapa. */}
    </div>
  );
}
